// import countries from './data/countries';
// import getCookie from './utils/getCookie';
// import setCookie, { defaultExpiration } from './utils/setCookie';

/**
 * A custom element for enhancing a static BBB my account link to a dialog window
 * that shows users their currently logged in information
 *
 * @module iabbb-my-account
 */

class MyAccount extends HTMLElement {
  constructor() {
    super();

    this.render = async () => {
      await this.initData();

      this.replaceLinkWithButton();
      this.updateButtonText();
    };
  }

  async connectedCallback() {
    await this.render();
  }

  initData = async () => {
    await fetch('/account/api/user/current', { credentials: 'include' }).then(async (res) => {
      if (res.status !== 200) {
        this.user = null;
        return;
      }

      const json = await res.json();

      if (!json?.success) {
        this.user = null;
        return;
      }

      this.user = json.data;
    });
  };

  buildAndOpenLegacyDialog = () => {
    const closeIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" fill="currentColor" aria-hidden="true" focusable="false" width="1em" height="1em"><path d="M7.5 105c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l151 151 151-151c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9l-151 151 151 151c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0l-151-151-151 151c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l151-151-151-151z"/></svg>`;
    const visuallyHiddenLabel = `<span class="visually-hidden">close outage disclaimer</span>`;

    const closeButton = document.createElement('button');
    closeButton.className = 'account-close-button bds-button-unstyled';
    closeButton.innerHTML = `${closeIcon}${visuallyHiddenLabel}`;

    const dialog = document.createElement('div');
    dialog.role = 'dialog';
    dialog.className = 'account-dialog';
    dialog.tabIndex = -1;

    // 👇 wrapper div aids in light dismiss
    const dialogContent = document.createElement('div');

    closeButton.onclick = () => {
      this.destroyLegacyDialog();
      this.querySelector('button').focus();
    };

    dialogContent.appendChild(closeButton);

    dialog.appendChild(dialogContent);

    const screen = document.createElement('div');
    screen.className = 'account-info';

    const heading = document.createElement('h2');
    heading.className = 'visually-hidden';
    heading.innerText = 'Login Information';

    const myAccountsHeading = document.createElement('h3');
    myAccountsHeading.innerText = 'My Accounts';

    const chevronRightIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512" fill="currentColor" aria-hidden="true" focusable="false" width="1em" height="1em"><path d="M311.1 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L243.2 256 73.9 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z"/></svg>`;
    const userIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" aria-hidden="true" focusable="false" height="1em" width="1em" fill="currentColor">
            <path d="M224 248a120 120 0 1 0 0-240 120 120 0 1 0 0 240zm-29.7 56C95.8 304 16 383.8 16 482.3 16 498.7 29.3 512 45.7 512l356.6 0c16.4 0 29.7-13.3 29.7-29.7 0-98.5-79.8-178.3-178.3-178.3l-59.4 0z"/>
          </svg>`;

    const personalAccountLink = document.createElement('a');
    personalAccountLink.className = 'account-link';
    personalAccountLink.href = '/account/login';
    personalAccountLink.innerHTML = `<span class="account-label">${userIcon}Personal Account</span>`;
    personalAccountLink.innerHTML += chevronRightIcon;

    const messageOutlineIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor" aria-hidden="true" focusable="false" width="1em" height="1em"><path d="M203.7 512.9s0 0 0 0l-37.8 26.7c-7.3 5.2-16.9 5.8-24.9 1.7S128 529 128 520l0-72-32 0c-53 0-96-43-96-96L0 128C0 75 43 32 96 32l320 0c53 0 96 43 96 96l0 224c0 53-43 96-96 96l-120.4 0-91.9 64.9zm64.3-104.1c8.1-5.7 17.8-8.8 27.7-8.8L416 400c26.5 0 48-21.5 48-48l0-224c0-26.5-21.5-48-48-48L96 80c-26.5 0-48 21.5-48 48l0 224c0 26.5 21.5 48 48 48l56 0c10.4 0 19.3 6.6 22.6 15.9 .9 2.5 1.4 5.2 1.4 8.1l0 49.7c32.7-23.1 63.3-44.7 91.9-64.9z"/></svg>`;

    const contactBbbLink = document.createElement('a');
    contactBbbLink.innerHTML = `${messageOutlineIcon} Contact My Local BBB`;
    contactBbbLink.className = 'contact-bbb-link';
    contactBbbLink.href = window.headerFooterData?.bbb?.url || '/bbb-directory';

    screen.appendChild(heading);

    if (this.user) {
      const userInfo = document.createElement('div');
      userInfo.className = 'user-info';

      const avatar = document.createElement('span');
      avatar.ariaHidden = true;
      avatar.className = 'account-avatar';
      avatar.innerText = `${this.user.firstName[0]}${this.user.lastName[0]}`;

      const userFullName = document.createElement('p');
      userFullName.innerText = `${this.user.firstName} ${this.user.lastName}`;
      userFullName.className = 'user-name';

      const userEmail = document.createElement('p');
      userEmail.innerText = this.user.email;
      userEmail.className = 'user-email';

      userInfo.appendChild(avatar);
      userInfo.appendChild(userFullName);
      userInfo.appendChild(userEmail);

      screen.appendChild(userInfo);
    }

    screen.appendChild(myAccountsHeading);

    screen.appendChild(personalAccountLink);

    if (window.headerFooterData?.bbb?.businessLoginUrl) {
      const universityIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor" aria-hidden="true" width="1em" height="1em"><path d="M271.9 20.2c-9.8-5.6-21.9-5.6-31.8 0l-224 128c-12.6 7.2-18.8 22-15.1 36S17.5 208 32 208l32 0 0 208 0 0-51.2 38.4C4.7 460.4 0 469.9 0 480 0 497.7 14.3 512 32 512l448 0c17.7 0 32-14.3 32-32 0-10.1-4.7-19.6-12.8-25.6l-51.2-38.4 0-208 32 0c14.5 0 27.2-9.8 30.9-23.8s-2.5-28.8-15.1-36l-224-128zM400 208l0 208-64 0 0-208 64 0zm-112 0l0 208-64 0 0-208 64 0zm-112 0l0 208-64 0 0-208 64 0zM256 96a32 32 0 1 1 0 64 32 32 0 1 1 0-64z"/></svg>`;

      const businessAccountLink = document.createElement('a');
      businessAccountLink.className = 'account-link';
      businessAccountLink.href = window.headerFooterData?.bbb?.businessLoginUrl;
      businessAccountLink.innerHTML = `<span class="account-label">${universityIcon}Business Account</span>`;
      businessAccountLink.innerHTML += chevronRightIcon;

      screen.appendChild(businessAccountLink);
    }

    screen.appendChild(contactBbbLink);

    if (this.user) {
      const signOutIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor" aria-hidden="true" focusable="false" width="1em" height="1em"><path d="M352 416c-17.7 0-32 14.3-32 32s14.3 32 32 32l64 0c53 0 96-43 96-96l0-256c0-53-43-96-96-96l-64 0c-17.7 0-32 14.3-32 32s14.3 32 32 32l64 0c17.7 0 32 14.3 32 32l0 256c0 17.7-14.3 32-32 32l-64 0zM9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l128 128c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.3 288 320 288c17.7 0 32-14.3 32-32s-14.3-32-32-32l-210.7 0 73.4-73.4c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-128 128z"/></svg>`;

      const signOutButton = document.createElement('button');
      signOutButton.className = 'sign-out-button bds-button-unstyled';
      signOutButton.innerHTML = `${signOutIcon} Sign Out`;
      signOutButton.onclick = async () => {
        await fetch('/account/api/auth/sign-out', { method: 'GET', credentials: 'include' }).then(() => {
          this.user = null;
          this.updateButtonText();
          this.destroyLegacyDialog();
          this.querySelector('button').focus();
        });
      };

      screen.appendChild(signOutButton);
    }

    dialogContent.appendChild(screen);

    this.updateDialogPosition(dialog);

    const dialogBackdrop = document.createElement('div');
    dialogBackdrop.className = 'account-dialog-backdrop';
    document.body.appendChild(dialogBackdrop);
    dialogBackdrop.addEventListener('click', () => {
      this.destroyLegacyDialog();
    });

    document.body.appendChild(dialog);

    dialog.focus();

    dialog.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.destroyLegacyDialog();
        this.querySelector('button').focus();
      }
    });

    window.addEventListener('resize', this.handleLegacyDialogPositionOnResize, { passive: true });
  };

  handleLegacyDialogPositionOnResize = () => {
    const dialogToListen = document.querySelector('.account-dialog');
    this.updateDialogPosition(dialogToListen);
  };

  updateDialogPosition = (dialog) => {
    const buttonBoundingClientRect = this.querySelector('button').getBoundingClientRect();

    dialog.style.left = `${buttonBoundingClientRect.left}px`;
    dialog.style.top = `${buttonBoundingClientRect.top}px`;
  };

  destroyLegacyDialog = () => {
    document.querySelector('.account-dialog-backdrop').remove();
    document.querySelector('.account-dialog').remove();

    // this.nonDialogElements stores all of the inert elements in memory when the dialog is created
    for (const nonDialogElement of this.nonDialogElements) {
      nonDialogElement.removeAttribute('inert');
    }
  };

  /**
   * Emit a custom event
   * @param {string} name - The event name
   * @param {object} detail - Any details to pass along with the event
   */
  emit = (name, detail = {}) => {
    // Create a new event
    const event = new CustomEvent(name, {
      bubbles: true,
      cancelable: true,
      composed: true,
      detail: detail,
    });

    // Dispatch the event
    return this.dispatchEvent(event);
  };

  replaceLinkWithButton = () => {
    const link = this.querySelector('a');

    if (!link) {
      return;
    }

    const btn = document.createElement('button');
    btn.innerHTML = link.innerHTML;

    btn.className = `${link.className} bds-button-unstyled`;
    btn.setAttribute('aria-expanded', 'false');
    btn.onclick = () => {
      // stored in memory so that we can remove `inert` when the dialog is closed
      this.nonDialogElements = document.querySelectorAll('body > :not(script)');

      for (const nonDialogElement of this.nonDialogElements) {
        nonDialogElement.setAttribute('inert', 'inert');
      }

      this.buildAndOpenLegacyDialog();
    };

    link.replaceWith(btn);
  };

  updateButtonText = () => {
    const button = this.querySelector('button');

    if (this.user) {
      const userIcon =
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" aria-hidden="true" focusable="false" height="1em" width="1em" fill="currentColor"><path d="M224 248a120 120 0 1 0 0-240 120 120 0 1 0 0 240zm-29.7 56C95.8 304 16 383.8 16 482.3 16 498.7 29.3 512 45.7 512l356.6 0c16.4 0 29.7-13.3 29.7-29.7 0-98.5-79.8-178.3-178.3-178.3l-59.4 0z"/></svg>';

      button.innerHTML = `${userIcon} My Account`;
    } else {
      const userOutlineIcon =
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" aria-hidden="true" focusable="false" fill="currentColor" width="1em" height="1em"><path d="M144 128a80 80 0 1 1 160 0 80 80 0 1 1 -160 0zm208 0a128 128 0 1 0 -256 0 128 128 0 1 0 256 0zM48 480c0-70.7 57.3-128 128-128l96 0c70.7 0 128 57.3 128 128l0 8c0 13.3 10.7 24 24 24s24-10.7 24-24l0-8c0-97.2-78.8-176-176-176l-96 0C78.8 304 0 382.8 0 480l0 8c0 13.3 10.7 24 24 24s24-10.7 24-24l0-8z"/></svg>';

      button.innerHTML = `${userOutlineIcon} Sign In`;
    }
  };
}

customElements.define('iabbb-my-account', MyAccount);
